/**
 * supabase/leads.js — CRUD de leads (getLeads/createLead/updateLead/deleteLead),
 * archivado e inscripción, lead_children, lead_activities y auto-archive.
 * Parte de la fachada src/lib/supabase.js.
 */

import { toDbStatus } from "../lead-stages";
import {
  getAsesorLevelTerritory,
  getLevelsForAsesor,
  getAsesorEmailByLevel,
} from "../asesor-level-config";
import {
  getSupabase,
  _hasFullAccess,
  getAsesorIdByEmail,
  getAsesorEmailById,
  _getCampusId,
  _isUuid,
} from "./client";

// ═══════════════════════════════════════════════════
// LEADS — Usa VIEW v_leads_completos
// ═══════════════════════════════════════════════════

/**
 * Obtiene leads con JOINs ya resueltos en la VIEW.
 *
 * ANTES: 2-3 queries + merge/dedup en JS
 * AHORA: 1 query a v_leads_completos con filtros PostgREST
 *
 * @param {string|null} asesorEmail - Email del asesor (null = admin, ve todo)
 * @param {Object} [options]
 * @param {string|null} [options.status]  - Filtrar por estado_lead
 * @param {string|null} [options.search]  - Búsqueda por nombre/teléfono
 * @param {number}      [options.limit]   - Máximo de resultados (default 500)
 * @param {number}      [options.offset]  - Offset para paginación
 * @returns {Promise<{data: Array, error: any, total: number}>}
 */
export async function getLeads(asesorEmail = null, options = {}) {
  const { status = null, search = null, limit = 500, offset = 0 } = options;
  const client = getSupabase();

  let query = client
    .from("v_leads_completos")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  // ── Filtro por asesor ──
  // Admin general + super admins ven TODO (sin filtro).
  // Asesoras ven sus leads asignados + leads sin asesor de su territorio.
  if (asesorEmail && !(await _hasFullAccess(asesorEmail))) {
    const asesorId = await getAsesorIdByEmail(asesorEmail);
    const territory = getAsesorLevelTerritory(asesorEmail);
    const levels = territory ? getLevelsForAsesor(asesorEmail) : null;

    if (!asesorId && !levels?.length) {
      return { data: [], error: null, total: 0 };
    }

    // Asesor ve:
    //   1. Sus leads asignados (asesor_id = X)
    //   2. Leads sin asesor DE SU TERRITORIO (nivel en sus niveles)
    //   3. Leads sin asesor Y SIN NIVEL DEFINIDO (bandeja común — típicamente
    //      prospectos recién registrados a mano que aún no tienen nivel)
    if (asesorId && levels?.length) {
      // PostgREST OR filter:
      // asesor_id = X
      //  OR (asesor_id IS NULL AND nivel_educativo IN (...))
      //  OR (asesor_id IS NULL AND nivel_educativo IS NULL)
      const levelsFilter = levels
        .map((l) => `nivel_educativo.eq.${l}`)
        .join(",");
      query = query.or(
        `asesor_id.eq.${asesorId},and(asesor_id.is.null,or(${levelsFilter},nivel_educativo.is.null))`,
      );
    } else if (asesorId) {
      query = query.eq("asesor_id", asesorId);
    } else if (levels?.length) {
      // Sin asesor_id en DB para esta cuenta pero sí niveles de territorio:
      // mostrar leads sin asesor de su territorio + sin asesor ni nivel.
      query = query
        .is("asesor_id", null)
        .or(
          `nivel_educativo.in.(${levels.join(",")}),nivel_educativo.is.null`,
        );
    }
  }

  // ── Filtro por estado ──
  if (status) {
    query = query.eq("estado_lead", status);
  }

  // ── Búsqueda ──
  if (search) {
    const term = `%${search}%`;
    query = query.or(
      `nombre_tutor.ilike.${term},telefono.ilike.${term},wa_jid.ilike.${term}`,
    );
  }

  const { data, error, count } = await query;

  if (error) {
    console.warn("[supabase] getLeads error:", error.message);
    return { data: [], error, total: 0 };
  }

  // Normalizar campos de la VIEW para compatibilidad con el frontend.
  // La VIEW usa campus_nombre/asesor_nombre, pero el frontend espera
  // lead.campus (string), lead.asesor_email, lead.asesor_nombre, etc.
  const normalized = (data ?? []).map((row) => ({
    ...row,
    // Campo "campus" como string (el frontend lo usa en filtros, display, etc.)
    campus: row.campus_nombre ?? row.campus ?? row.campus_id ?? null,
    // Compat: el frontend también accede a estos directamente
    campus_codigo: row.campus_codigo ?? null,
    campus_estado: row.campus_estado ?? null,
    campus_municipio: row.campus_municipio ?? null,
    asesor_email: row.asesor_email ?? null,
    asesor_nombre: row.asesor_nombre ?? null,
  }));

  return { data: normalized, error: null, total: count ?? normalized.length };
}

// ═══════════════════════════════════════════════════
// UPDATE LEAD — Usa TABLE leads (trigger auto updated_at)
// ═══════════════════════════════════════════════════

/** Campos permitidos para update (whitelist) */
const _UPDATABLE = new Set([
  "wa_jid",
  "instancia",
  "nombre_tutor",
  "telefono",
  "email",
  "nivel_educativo",
  "estado",
  "municipio",
  "campus_id",
  "asesor_id",
  "estado_lead",
  "calidad_lead",
  "datos_completos",
  "intervencion_humana",
  "fecha_cita",
  "cita_confirmada",
  "ultima_interaccion",
  "win_probability",
  "next_step",
  "value",
  "source",
  // Campos del sistema de recordatorios
  "recordatorio_1_at",
  "recordatorio_2_at",
  "recordatorio_3_at",
  "fecha_archivado",
  // Campo de inscripción
  "fecha_inscripcion",
  "inscripcion_confirmada_at",
  // Campos de datos del alumno
  "nombre_alumno",
  "grado_especifico",
  "es_familia",
  "num_hijos",
  "tema_consulta",
  "notas",
]);

const _VALID_CALIDAD = new Set(["frio", "tibio", "caliente", "spam"]);

/**
 * Actualiza un lead con validaciones.
 * El trigger trg_leads_updated_at pone updated_at automáticamente.
 *
 * @param {string} id   - UUID del lead
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<{data: Object|null, error: any}>}
 */
export async function updateLead(id, updates) {
  const raw = { ...updates };
  const client = getSupabase();

  // Normalizar estado
  if (raw.estado_lead) raw.estado_lead = toDbStatus(raw.estado_lead);

  // No forzar intervencion_humana automáticamente.
  // El asesor controla manualmente el modo humano/IA desde el toggle.

  // Mapear campos legacy
  if (raw.fecha_proximo_seguimiento && !raw.fecha_cita)
    raw.fecha_cita = raw.fecha_proximo_seguimiento;
  if (raw.ultima_actividad && !raw.ultima_interaccion)
    raw.ultima_interaccion = raw.ultima_actividad;

  // Normalizar nivel educativo
  if (raw.nivel_educativo !== undefined) {
    const nv = String(raw.nivel_educativo ?? "")
      .trim()
      .toLowerCase();
    if (!nv) {
      // Cadena vacía → no enviar (evita violar la constraint)
      delete raw.nivel_educativo;
    } else {
      // Mapear variantes comunes al valor correcto de la DB
      const NIVEL_MAP = {
        materna: "maternal",
        prepa: "preparatoria",
        "pre-first": "prefirst",
        "pre first": "prefirst",
        jardin: "kinder",
        jardín: "kinder",
        kindergarden: "kinder",
        preescolar: "kinder",
      };
      raw.nivel_educativo = NIVEL_MAP[nv] || nv;
    }
  }

  // Validar calidad
  if (raw.calidad_lead) {
    raw.calidad_lead = String(raw.calidad_lead).trim().toLowerCase();
    if (!_VALID_CALIDAD.has(raw.calidad_lead)) raw.calidad_lead = "tibio";
  }

  // Auto-reasignar asesor cuando cambia nivel_educativo
  if (raw.nivel_educativo && !raw.asesor_id && !raw.asesor_email && !raw.asesor) {
    const targetEmail = getAsesorEmailByLevel(raw.nivel_educativo);
    if (targetEmail) {
      const aid = await getAsesorIdByEmail(targetEmail);
      if (aid) raw.asesor_id = aid;
    }
  }

  // Resolver asesor email → UUID
  if (!raw.asesor_id) {
    const email = raw.asesor_email || raw.asesor || null;
    if (email) {
      const aid = await getAsesorIdByEmail(email);
      if (aid) raw.asesor_id = aid;
    }
  }

  // Auto-alinear instancia basada en el asesor asignado o el nivel educativo (si no viene explícita)
  if (!raw.instancia) {
    if (raw.asesor_id) {
      const email = await getAsesorEmailById(raw.asesor_id);
      if (email) {
        if (email === "gaby@tepeyac.mx") raw.instancia = "tepeyec-gaby";
        else if (email === "lulu@tepeyac.mx") raw.instancia = "tepeyact-produccion";
      }
    } else if (raw.nivel_educativo) {
      const targetEmail = getAsesorEmailByLevel(raw.nivel_educativo);
      if (targetEmail === "gaby@tepeyac.mx") raw.instancia = "tepeyec-gaby";
      else if (targetEmail === "lulu@tepeyac.mx") raw.instancia = "tepeyact-produccion";
    }
  }

  // Resolver campus código/nombre → UUID
  if (!raw.campus_id) {
    const cv = raw.campus || raw.campus_codigo || raw.campus_code || null;
    if (cv) {
      const cid = await _getCampusId(cv);
      if (cid) raw.campus_id = cid;
    }
  }

  // Whitelist de campos
  const payload = {};
  for (const [k, v] of Object.entries(raw)) {
    if (_UPDATABLE.has(k) && v !== undefined) payload[k] = v;
  }

  if (Object.keys(payload).length === 0) {
    return {
      data: null,
      error: { message: "No hay campos válidos para actualizar" },
    };
  }

  const { data, error } = await client
    .from("leads")
    .update(payload)
    .eq("id", id)
    .select(
      `*, campus:campus_id(id, nombre, codigo, estado, municipio), asesor:asesor_id(id, nombre, email)`,
    )
    .single();

  if (error) {
    // Si el error es por columnas que no existen aún (migración pendiente),
    // reintentar sin esas columnas
    const MIGRATION_COLUMNS = [
      "recordatorio_1_at",
      "recordatorio_2_at",
      "recordatorio_3_at",
      "fecha_archivado",
    ];
    if (
      error.message &&
      error.message.includes("column") &&
      error.message.includes("schema cache")
    ) {
      const cleanPayload = { ...payload };
      let removed = false;
      for (const col of MIGRATION_COLUMNS) {
        if (col in cleanPayload) {
          delete cleanPayload[col];
          removed = true;
        }
      }
      if (removed && Object.keys(cleanPayload).length > 0) {
        console.warn(
          "[supabase] Reintentando updateLead sin columnas de migración pendiente...",
        );
        const { data: retryData, error: retryError } = await client
          .from("leads")
          .update(cleanPayload)
          .eq("id", id)
          .select(
            `*, campus:campus_id(id, nombre, codigo, estado, municipio), asesor:asesor_id(id, nombre, email)`,
          )
          .single();
        if (!retryError) {
          // Reutilizar el path de normalización con retryData
          const campusObj2 = retryData?.campus ?? null;
          const asesorObj2 = retryData?.asesor ?? null;
          return {
            data: {
              ...retryData,
              campus: campusObj2?.nombre ?? retryData?.campus ?? null,
              campus_nombre: campusObj2?.nombre ?? null,
              campus_codigo: campusObj2?.codigo ?? null,
              campus_estado: campusObj2?.estado ?? null,
              campus_municipio: campusObj2?.municipio ?? null,
              asesor_email:
                asesorObj2?.email ?? retryData?.asesor_email ?? null,
              asesor_nombre:
                asesorObj2?.nombre ?? retryData?.asesor_nombre ?? null,
            },
            error: null,
          };
        }
        console.warn("[supabase] updateLead retry error:", retryError.message);
        return { data: null, error: retryError };
      }
    }
    console.warn("[supabase] updateLead error:", error.message);
    return { data: null, error };
  }

  // Normalizar para compat: el frontend espera lead.campus como string
  // pero también lead.campus como objeto en AdminPage (lead.campus.nombre)
  const campusObj = data?.campus ?? null;
  const asesorObj = data?.asesor ?? null;

  const normalized = {
    ...data,
    // String shortcuts (para Kanban, cards, filtros)
    campus: campusObj?.nombre ?? data?.campus ?? null,
    campus_nombre: campusObj?.nombre ?? null,
    campus_codigo: campusObj?.codigo ?? null,
    campus_estado: campusObj?.estado ?? null,
    campus_municipio: campusObj?.municipio ?? null,
    asesor_email: asesorObj?.email ?? data?.asesor_email ?? null,
    asesor_nombre: asesorObj?.nombre ?? data?.asesor_nombre ?? null,
  };

  return { data: normalized, error: null };
}

/**
 * Crea un lead manualmente (registro por recomendacion, sin WhatsApp).
 * @param {Object} data - Campos del lead
 * @returns {Promise<{data: Object|null, error: any}>}
 */
export async function createLead(data) {
  const raw = { ...data };
  const client = getSupabase();

  if (raw.estado_lead) {
    raw.estado_lead = toDbStatus(raw.estado_lead);
  } else {
    raw.estado_lead = "nuevo";
  }

  if (raw.nivel_educativo) {
    const nv = String(raw.nivel_educativo).trim().toLowerCase();
    const NIVEL_MAP = {
      materna: "maternal",
      prepa: "preparatoria",
      "pre-first": "prefirst",
      "pre first": "prefirst",
      jardin: "kinder",
      jardín: "kinder",
      kindergarden: "kinder",
      preescolar: "kinder",
    };
    raw.nivel_educativo = NIVEL_MAP[nv] || nv;
  }

  if (raw.calidad_lead) {
    raw.calidad_lead = String(raw.calidad_lead).trim().toLowerCase();
    if (!_VALID_CALIDAD.has(raw.calidad_lead)) raw.calidad_lead = "tibio";
  }

  if (raw.nivel_educativo && !raw.asesor_id) {
    const targetEmail = getAsesorEmailByLevel(raw.nivel_educativo);
    if (targetEmail) {
      const aid = await getAsesorIdByEmail(targetEmail);
      if (aid) raw.asesor_id = aid;
    }
  }

  if (raw.campus_id && !_isUuid(raw.campus_id)) {
    const cid = await _getCampusId(raw.campus_id);
    if (cid) raw.campus_id = cid;
    else delete raw.campus_id;
  }

  raw.intervencion_humana = true;

  // Auto-generar wa_jid a partir del teléfono si no viene
  if (!raw.wa_jid && raw.telefono) {
    let cleanPhone = String(raw.telefono).replace(/\D/g, "");
    // Si no tiene código de país, agregar +521 (México móvil)
    if (cleanPhone.length === 10) cleanPhone = "521" + cleanPhone;
    else if (cleanPhone.length === 12 && cleanPhone.startsWith("52")) cleanPhone = cleanPhone;
    else if (cleanPhone.length === 13 && cleanPhone.startsWith("521")) cleanPhone = cleanPhone;
    raw.wa_jid = `${cleanPhone}@s.whatsapp.net`;
  }

  // Instancia derivada del nivel educativo o del asesor asignado (si no se especifica explícita).
  if (!raw.instancia) {
    if (raw.nivel_educativo) {
      const targetEmail = getAsesorEmailByLevel(raw.nivel_educativo);
      if (targetEmail === "gaby@tepeyac.mx") raw.instancia = "tepeyec-gaby";
      else if (targetEmail === "lulu@tepeyac.mx") raw.instancia = "tepeyact-produccion";
    } else if (raw.asesor_id) {
      const email = await getAsesorEmailById(raw.asesor_id);
      if (email) {
        if (email === "gaby@tepeyac.mx") raw.instancia = "tepeyec-gaby";
        else if (email === "lulu@tepeyac.mx") raw.instancia = "tepeyact-produccion";
      }
    }
  }

  // Source para leads manuales
  if (!raw.source) raw.source = "manual";

  const INSERTABLE = new Set([
    "nombre_tutor", "nombre_alumno", "telefono", "email",
    "nivel_educativo", "grado_especifico", "es_familia", "num_hijos",
    "estado", "municipio", "notas",
    "calidad_lead", "estado_lead", "asesor_id", "campus_id",
    "intervencion_humana", "wa_jid", "instancia", "source", "tema_consulta",
  ]);

  const payload = {};
  for (const [k, v] of Object.entries(raw)) {
    if (INSERTABLE.has(k) && v !== undefined && v !== "") payload[k] = v;
  }

  const { data: created, error } = await client
    .from("leads")
    .insert(payload)
    .select(
      `*, campus:campus_id(id, nombre, codigo, estado, municipio), asesor:asesor_id(id, nombre, email)`,
    )
    .single();

  if (error) {
    console.warn("[supabase] createLead error:", error.message);
    return { data: null, error };
  }

  const campusObj = created?.campus ?? null;
  const asesorObj = created?.asesor ?? null;

  const normalized = {
    ...created,
    campus: campusObj?.nombre ?? created?.campus ?? null,
    campus_nombre: campusObj?.nombre ?? null,
    campus_codigo: campusObj?.codigo ?? null,
    campus_estado: campusObj?.estado ?? null,
    campus_municipio: campusObj?.municipio ?? null,
    asesor_email: asesorObj?.email ?? created?.asesor_email ?? null,
    asesor_nombre: asesorObj?.nombre ?? created?.asesor_nombre ?? null,
  };

  return { data: normalized, error: null };
}

/** Elimina un lead y toda su memoria de chat. Usa: TABLE leads + messages + n8n_chat_histories */
export async function deleteLead(leadId) {
  const client = getSupabase();

  const { data: lead } = await client
    .from("leads")
    .select("wa_jid")
    .eq("id", leadId)
    .single();

  if (lead?.wa_jid) {
    // 1. Borrar memoria del bot (LangChain)
    await client.from("n8n_chat_histories").delete().eq("session_id", lead.wa_jid);
    // 2. Borrar mensajes huérfanos del mismo wa_jid (los que tienen lead_id null
    //    porque n8n los guardó antes de resolver el lead). CASCADE no los cubre.
    await client.from("messages").delete().eq("wa_jid", lead.wa_jid);
  }

  return await client.from("leads").delete().eq("id", leadId);
}

/**
 * Archiva un lead.
 * - Si el lead YA está "inscrito": confirma la inscripción (queda en el
 *   histórico de inscritos, NO se manda a perdido).
 * - Caso normal: cambia estado_lead a "perdido".
 */
export async function archiveLead(leadId, extra = {}) {
  // Verificar el estado actual primero
  const { data: lead } = await getSupabase()
    .from("leads")
    .select("estado_lead")
    .eq("id", leadId)
    .maybeSingle();

  if (lead?.estado_lead === "inscrito") {
    return await getSupabase()
      .from("leads")
      .update({
        inscripcion_confirmada_at: new Date().toISOString(),
        ...extra,
      })
      .eq("id", leadId);
  }
  return updateLead(leadId, { estado_lead: "perdido", ...extra });
}

/**
 * Confirma la inscripción de un lead. Lo saca del Kanban (deja de aparecer
 * en la columna "Inscrito") y lo marca con la fecha de confirmación, que se
 * usa para agrupar en el InscritosModal y para las métricas mensuales.
 *
 * Usa .select() para devolver la fila actualizada y poder verificar que
 * realmente cambió — si una policy de RLS bloquea el UPDATE, Supabase no
 * devuelve error pero data llega vacío. Tratamos ese caso como error.
 */
export async function confirmInscripcion(leadId) {
  const { data, error } = await getSupabase()
    .from("leads")
    .update({ inscripcion_confirmada_at: new Date().toISOString() })
    .eq("id", leadId)
    .select()
    .maybeSingle();

  if (error) return { data: null, error };
  if (!data) {
    return {
      data: null,
      error: new Error("La inscripción no se actualizó. Probablemente sea un permiso de la BD (RLS). Avísale a Yair."),
    };
  }
  return { data, error: null };
}

// ═══════════════════════════════════════════════════
// LEAD CHILDREN — Tabla relacional para familias
// ═══════════════════════════════════════════════════

/** Lista los hijos registrados para un lead, en orden. */
export async function getLeadChildren(leadId) {
  if (!leadId) return [];
  const { data, error } = await getSupabase()
    .from("lead_children")
    .select("*")
    .eq("lead_id", leadId)
    .order("orden", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) {
    console.warn("[supabase] getLeadChildren error:", error.message);
    return [];
  }
  return data || [];
}

/** Crea un hijo. El trigger actualiza leads.num_hijos/es_familia. */
export async function createLeadChild(leadId, child) {
  if (!leadId) return { data: null, error: { message: "leadId requerido" } };
  const payload = {
    lead_id: leadId,
    nombre: String(child.nombre || "").trim(),
    nivel_educativo: child.nivel_educativo || null,
    grado_especifico: child.grado_especifico || null,
    edad: child.edad != null && child.edad !== "" ? Number(child.edad) : null,
    sexo: child.sexo || null,
    notas: child.notas || null,
    orden: child.orden ?? 0,
  };
  if (!payload.nombre) return { data: null, error: { message: "Nombre requerido" } };
  return await getSupabase().from("lead_children").insert(payload).select().single();
}

/** Actualiza un hijo. */
export async function updateLeadChild(id, updates) {
  const payload = {};
  const fields = ["nombre", "nivel_educativo", "grado_especifico", "edad", "sexo", "notas", "orden"];
  for (const k of fields) {
    if (k in updates) payload[k] = updates[k] === "" ? null : updates[k];
  }
  return await getSupabase()
    .from("lead_children")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
}

/** Elimina un hijo. */
export async function deleteLeadChild(id) {
  return await getSupabase().from("lead_children").delete().eq("id", id);
}

/**
 * Cuenta total de hijos registrados en lead_children.
 * Opcionalmente filtra por asesor o campus a través del join al lead padre.
 * @param {Object} [options]
 * @param {string|null} [options.asesorId]
 * @param {string|null} [options.campusId]
 */
export async function countLeadChildren({ asesorId = null, campusId = null } = {}) {
  const client = getSupabase();

  // Caso simple: sin filtros → count directo
  if (!asesorId && !campusId) {
    const { count, error } = await client
      .from("lead_children")
      .select("id", { count: "exact", head: true });
    if (error) {
      console.warn("[supabase] countLeadChildren error:", error.message);
      return 0;
    }
    return count || 0;
  }

  // Con filtros → join con leads
  let query = client
    .from("lead_children")
    .select("id, lead:lead_id!inner(asesor_id, campus_id)", { count: "exact", head: true });

  if (asesorId) query = query.eq("lead.asesor_id", asesorId);
  if (campusId) query = query.eq("lead.campus_id", campusId);

  const { count, error } = await query;
  if (error) {
    console.warn("[supabase] countLeadChildren error:", error.message);
    return 0;
  }
  return count || 0;
}

// ═══════════════════════════════════════════════════
// LEAD ACTIVITIES — Historial/timeline por lead
// ═══════════════════════════════════════════════════

/**
 * Registra una actividad en el historial del lead.
 * No-op silencioso si falla (no queremos romper la UX por un log).
 */
export async function logLeadActivity(leadId, activity) {
  if (!leadId || !activity?.tipo || !activity?.titulo) return { data: null };
  try {
    const payload = {
      lead_id: leadId,
      tipo: activity.tipo,
      titulo: String(activity.titulo).slice(0, 200),
      descripcion: activity.descripcion || null,
      metadata: activity.metadata || null,
      asesor_id: activity.asesor_id || null,
    };
    const { data, error } = await getSupabase()
      .from("lead_activities")
      .insert(payload)
      .select()
      .single();
    if (error) {
      console.warn("[supabase] logLeadActivity error:", error.message);
      return { data: null, error };
    }
    return { data, error: null };
  } catch (err) {
    console.warn("[supabase] logLeadActivity exception:", err?.message);
    return { data: null, error: err };
  }
}

/** Obtiene el timeline de actividades de un lead (recientes primero). */
export async function getLeadActivities(leadId, limit = 100) {
  if (!leadId) return [];
  const { data, error } = await getSupabase()
    .from("lead_activities")
    .select("*, asesor:asesor_id(id, nombre, email)")
    .eq("lead_id", leadId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    console.warn("[supabase] getLeadActivities error:", error.message);
    return [];
  }
  return data || [];
}

/** Elimina una entrada manual del historial. */
export async function deleteLeadActivity(id) {
  return await getSupabase().from("lead_activities").delete().eq("id", id);
}


// ═══════════════════════════════════════════════════
// AUTO ARCHIVE — Reglas CAP-friendly
// ═══════════════════════════════════════════════════
//
// REGLA: Solo se auto-archivan leads que cumplen TODAS estas condiciones:
//   1. estado_lead ∈ ('nuevo', 'contactado')   ← etapas tempranas
//   2. ultima_interaccion < hace 30 días        ← umbral generoso
//   3. fecha_cita IS NULL                       ← sin cita programada
//   4. intervencion_humana = false              ← CAP no ha tomado control
//   5. calidad_lead != 'caliente'               ← no marcado como prioritario
//
// Cualquier lead en cita / interesado / entrega_resultado / pendiente_pago /
// inscrito JAMÁS se auto-archiva — son leads que la CAP ya invirtió tiempo en
// mover y suelen cerrarse fuera de WhatsApp (teléfono, en persona).
//
// El RPC server-side `archive_inactive_leads` (5 días hardcodeado) NO se usa;
// hacemos el filtrado en cliente para tener control total de las reglas.

const AUTO_ARCHIVE_DAYS = 30;
const AUTO_ARCHIVE_STAGES = ["nuevo", "contactado"];

/**
 * Archiva leads inactivos respetando reglas que protegen al trabajo de la CAP.
 * Ver comentario de bloque arriba para las condiciones exactas.
 *
 * @param {number} [_thresholdDays] - Ignorado (compat legacy)
 * @param {Object} [_extra] - Ignorado (compat legacy)
 * @returns {Promise<{archivedIds: Array, archivedCount: number, error: any}>}
 */
export async function autoArchiveStaleLeads(_thresholdDays, _extra) {
  const client = getSupabase();
  const cutoffIso = new Date(Date.now() - AUTO_ARCHIVE_DAYS * 24 * 60 * 60 * 1000).toISOString();

  // 1. Traer IDs candidatos. Filtramos en SQL todo lo que se puede.
  //    `calidad_lead != 'caliente'` también acepta NULL gracias a `or(...is.null)`.
  const { data: candidates, error: selErr } = await client
    .from("leads")
    .select("id, calidad_lead, intervencion_humana")
    .in("estado_lead", AUTO_ARCHIVE_STAGES)
    .lt("ultima_interaccion", cutoffIso)
    .is("fecha_cita", null);

  if (selErr) {
    console.warn("[supabase] autoArchive select error:", selErr.message);
    return { archivedIds: [], archivedCount: 0, error: selErr };
  }

  // 2. Filtro fino en cliente (boolean + calidad pueden ser null/varios casts).
  const toArchive = (candidates ?? []).filter((l) => {
    const human = l.intervencion_humana === true || l.intervencion_humana === "true" || l.intervencion_humana === 1;
    if (human) return false;
    const cal = String(l.calidad_lead || "").toLowerCase();
    if (cal === "caliente") return false;
    return true;
  });

  if (!toArchive.length) {
    return { archivedIds: [], archivedCount: 0, error: null };
  }

  const ids = toArchive.map((l) => l.id);
  const nowIso = new Date().toISOString();

  // 3. Update batch.
  const { error: updErr } = await client
    .from("leads")
    .update({ estado_lead: "inactivo", fecha_archivado: nowIso })
    .in("id", ids);

  if (updErr) {
    console.warn("[supabase] autoArchive update error:", updErr.message);
    return { archivedIds: [], archivedCount: 0, error: updErr };
  }

  return { archivedIds: ids, archivedCount: ids.length, error: null };
}
